import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Plus, LayoutGrid, List } from 'lucide-react';
import { TaskBoard } from '@/components/tasks/TaskBoard';
import { TaskFormDialog } from '@/components/tasks/TaskFormDialog';
import { TaskFilterBar } from '@/components/tasks/TaskFilterBar';
import { useTasks } from '@/hooks/useTasks';
import { useTaskFilters } from '@/hooks/useTaskFilters';
import { useProjects } from '@/hooks/useProjects';
import { useContacts } from '@/hooks/useContacts';
import { useIsMobile } from '@/hooks/use-mobile';

export default function Tasks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<'board' | 'list'>('board');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Handle deep link action for app shortcuts
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create') {
      setShowCreateDialog(true);
      // Clear params after opening dialog
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { tasks: allTasks, isLoading } = useTasks();
  const { projects } = useProjects();
  const { contacts } = useContacts();
  const { filters, updateFilters, clearFilters, activeFilterCount, applyFilters } = useTaskFilters();

  const filteredTasks = useMemo(() => applyFilters(allTasks), [allTasks, applyFilters]);
  
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    allTasks.forEach(task => {
      (task.tags || []).forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [allTasks]);

  const contactsForFilter = useMemo(() => 
    contacts.map(c => ({ id: c.id, name: c.name })),
    [contacts]
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Tasks</h1>
            <p className="text-muted-foreground mt-2">
              {filteredTasks.length} {filteredTasks.length === 1 ? 'task' : 'tasks'}
              {activeFilterCount > 0 && ' (filtered)'}
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              variant="outline" 
              onClick={() => setView(view === 'board' ? 'list' : 'board')}
              className="flex-1 sm:flex-none"
            >
              {view === 'board' ? (
                <><List className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">List View</span></>
              ) : (
                <><LayoutGrid className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Board View</span></>
              )}
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} className="flex-1 sm:flex-none">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">New Task</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>

      {/* Filter Bar */}
      <TaskFilterBar
        filters={filters}
        onFilterChange={updateFilters}
        onClearFilters={clearFilters}
        activeFilterCount={activeFilterCount}
        projects={projects}
        contacts={contactsForFilter}
        availableTags={availableTags}
      />

      {/* Board */}
      <TaskBoard view={view} tasks={filteredTasks} />

        <TaskFormDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
        />
      </div>
    </DashboardLayout>
  );
}
